"use client";

import React, { useState, useMemo } from "react";
import { Search, Copy, Check, Sparkles, ArrowRight, Zap } from "lucide-react";

interface SearchItem {
  id: string;
  title: string;
  category: string;
  description: string;
  actionText?: string;
  copyValue?: string;
}

const ITEMS: SearchItem[] = [
  {
    id: "setup",
    title: "Projekt schnell starten",
    category: "Setup",
    description: "Der Standard-Befehl für ein neues React-Projekt mit Next.js.",
    actionText: "Befehl kopieren",
    copyValue: "npx create-next-app@latest my-app",
  },
  {
    id: "a11y",
    title: "Barrierefreiheit & Kontraste prüfen",
    category: "Qualität",
    description: "Checkliste für gute Lesbarkeit, Screenreader und Tastaturbedienung.",
    actionText: "Checkliste ansehen",
  },
  {
    id: "darkmode",
    title: "Dunkelmodus automatisch umschalten",
    category: "Design",
    description: "Passt Farben automatisch an die Systemeinstellung des Nutzers an.",
    actionText: "Code anzeigen",
    copyValue: "@media (prefers-color-scheme: dark) { ... }",
  },
  {
    id: "images",
    title: "Bilder mit WebP & AVIF beschleunigen",
    category: "Ladezeit",
    description: "Moderne Bildformate sparen bis zu 70% Dateigröße und Datenvolumen.",
    actionText: "Tipp merken",
  },
  {
    id: "search",
    title: "Sofortsuche im Browser",
    category: "Architektur",
    description: "Ergebnisse direkt beim Tippen anzeigen – ohne Wartepause zum Server.",
    actionText: "Mehr erfahren",
  },
];

const SUGGESTIONS = ["Setup", "Barrierefreiheit", "Design", "Ladezeit"];

export default function IntuitiveSearchPlayground() {
  const [query, setQuery] = useState("");
  const [copiedId, setCopiedId] = useState<string | null>(null);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return ITEMS.slice(0, 3);

    return ITEMS.filter(
      (item) =>
        item.title.toLowerCase().includes(q) ||
        item.category.toLowerCase().includes(q) ||
        item.description.toLowerCase().includes(q)
    );
  }, [query]);

  const handleCopy = (id: string, text: string) => {
    navigator.clipboard.writeText(text);
    setCopiedId(id);
    setTimeout(() => setCopiedId(null), 1800);
  };

  return (
    <div className="my-8 rounded-2xl border border-neutral-200/90 bg-gradient-to-b from-white via-neutral-50/50 to-neutral-100/30 p-5 shadow-xs sm:p-6 dark:border-neutral-800 dark:from-neutral-900/90 dark:via-neutral-900/50 dark:to-neutral-950">
      {/* Header */}
      <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <span className="flex h-7 w-7 items-center justify-center rounded-lg bg-blue-500/10 text-blue-600 dark:bg-blue-400/10 dark:text-blue-400">
            <Zap className="h-4 w-4" />
          </span>
          <h3 className="font-heading text-base font-bold text-neutral-900 dark:text-white sm:text-lg">
            Mini-Vorschau: So fühlt sich Sofortsuche an
          </h3>
        </div>
        <span className="inline-flex items-center gap-1 rounded-full bg-emerald-500/10 px-2.5 py-0.5 font-mono text-[11px] font-medium text-emerald-600 dark:text-emerald-400">
          <span className="h-1.5 w-1.5 rounded-full bg-emerald-500 animate-pulse" />
          0 ms Wartezeit
        </span>
      </div>

      {/* Search Input */}
      <div className="relative mb-3">
        <Search className="absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-neutral-400" />
        <input
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Tippe z. B. 'Setup', 'Farbe', 'Barriere'..."
          className="w-full rounded-xl border border-neutral-300 bg-white py-2.5 pl-10 pr-24 text-sm text-neutral-900 placeholder-neutral-400 shadow-xs transition-all focus:border-blue-500 focus:outline-hidden focus:ring-2 focus:ring-blue-500/20 dark:border-neutral-700 dark:bg-neutral-850 dark:text-white dark:placeholder-neutral-500"
        />
        {query && (
          <button
            onClick={() => setQuery("")}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-neutral-400 hover:text-neutral-600 dark:hover:text-neutral-200"
          >
            Löschen
          </button>
        )}
      </div>

      {/* Quick Pills */}
      <div className="mb-4 flex flex-wrap items-center gap-1.5 text-xs">
        <span className="text-neutral-400 dark:text-neutral-500 mr-1">Beispiele:</span>
        {SUGGESTIONS.map((tag) => (
          <button
            key={tag}
            onClick={() => setQuery(tag)}
            className={`rounded-full px-2.5 py-0.5 transition-colors ${
              query.toLowerCase() === tag.toLowerCase()
                ? "bg-blue-600 text-white font-medium"
                : "bg-neutral-100 text-neutral-600 hover:bg-neutral-200 dark:bg-neutral-800 dark:text-neutral-300 dark:hover:bg-neutral-700"
            }`}
          >
            {tag}
          </button>
        ))}
      </div>

      {/* Results List */}
      <div className="space-y-2">
        {filtered.length > 0 ? (
          filtered.map((item) => (
            <div
              key={item.id}
              className="group flex flex-col sm:flex-row sm:items-center justify-between gap-2 rounded-xl border border-neutral-200/70 bg-white p-3.5 transition-all hover:border-neutral-300 hover:shadow-xs dark:border-neutral-800/80 dark:bg-neutral-850/60 dark:hover:border-neutral-700"
            >
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 mb-1">
                  <span className="rounded bg-neutral-100 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-neutral-600 dark:bg-neutral-800 dark:text-neutral-400">
                    {item.category}
                  </span>
                  <h4 className="font-heading text-sm font-bold text-neutral-900 dark:text-white truncate">
                    {item.title}
                  </h4>
                </div>
                <p className="text-xs text-neutral-600 dark:text-neutral-400 line-clamp-1">
                  {item.description}
                </p>
              </div>

              {item.copyValue ? (
                <button
                  onClick={() => handleCopy(item.id, item.copyValue!)}
                  className={`inline-flex shrink-0 items-center gap-1 rounded-lg px-2.5 py-1 text-xs font-medium transition-colors ${
                    copiedId === item.id
                      ? "bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border border-emerald-500/20"
                      : "bg-neutral-100 text-neutral-700 hover:bg-neutral-200 dark:bg-neutral-800 dark:text-neutral-300 dark:hover:bg-neutral-700"
                  }`}
                >
                  {copiedId === item.id ? (
                    <>
                      <Check className="h-3.5 w-3.5 text-emerald-500" />
                      <span>Kopiert!</span>
                    </>
                  ) : (
                    <>
                      <Copy className="h-3.5 w-3.5 opacity-60" />
                      <span>{item.actionText || "Kopieren"}</span>
                    </>
                  )}
                </button>
              ) : (
                <div className="inline-flex shrink-0 items-center gap-1 text-xs text-blue-600 dark:text-blue-400 font-medium opacity-80 group-hover:opacity-100">
                  <span>{item.actionText}</span>
                  <ArrowRight className="h-3 w-3 transition-transform group-hover:translate-x-0.5" />
                </div>
              )}
            </div>
          ))
        ) : (
          <div className="rounded-xl border border-dashed border-neutral-300 p-6 text-center text-sm text-neutral-500 dark:border-neutral-700 dark:text-neutral-400">
            Keine Treffer für „{query}“. Versuche einen anderen Begriff.
          </div>
        )}
      </div>

      {/* Footer hint */}
      <div className="mt-3.5 flex items-center justify-between text-[11px] text-neutral-400 dark:text-neutral-500 pt-2 border-t border-neutral-100 dark:border-neutral-800/60">
        <span>Tipp: Kein Seitenwechsel nötig – die Antwort ist sofort da.</span>
        <span>Reaktionszeit: Direkt im Browser</span>
      </div>
    </div>
  );
}
